import { Paperclip, Send } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { RichTextarea, RichTextareaHandle } from '@/components/custom/rich-textarea'
import { EmojiPicker } from '@/components/ui/emoji-picker'

import { ReplyPreview } from './reply-preview'

interface MobileChatInputProps {
  newMessage: string
  selectedFile: File | null
  isUploadingFile: boolean
  pendingTargetUserId: number | undefined
  isCreatingConversation: boolean
  isLoading: boolean
  selectedConversationId: number | undefined
  targetUserName: string
  replyingToMessage: {
    id: number
    content: string
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    type: string
    fileUrl?: string
    fileName?: string
  } | null
  onSendMessage: () => void
  onReplyMessage: () => void
  onFileSelect: (file?: File) => void
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => void
  onTyping: (html: string) => void
  onInputFocus: () => void
  onCancelReply: () => void
  insertEmoji: (emoji: string) => void
  insertCustomEmoji: (emoji: { id: number; name: string; imageUrl: string }) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  inputRef: React.RefObject<RichTextareaHandle>
  mentionableUsers: Array<{
    id: number
    firstName: string
    lastName: string
    avatar?: string
    profileId?: string
  }>
}

export const MobileChatInput: React.FC<MobileChatInputProps> = ({
  newMessage,
  selectedFile,
  isUploadingFile,
  pendingTargetUserId,
  isCreatingConversation,
  isLoading,
  selectedConversationId,
  targetUserName,
  replyingToMessage,
  onSendMessage,
  onReplyMessage,
  onFileSelect,
  onFileChange,
  onRemoveFile,
  onTyping,
  onInputFocus,
  onCancelReply,
  insertEmoji,
  insertCustomEmoji,
  fileInputRef,
  inputRef,
  mentionableUsers,
}) => {
  const isDisabled = !selectedConversationId || isLoading || isCreatingConversation || isUploadingFile
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null)
  const [selectedMentionIndex, setSelectedMentionIndex] = React.useState(0)

  const getPlainTextFromHtml = React.useCallback((html: string) => {
    if (typeof window === 'undefined') {
      return html.replace(/<[^>]*>/g, ' ')
    }
    const element = document.createElement('div')
    element.innerHTML = html
    return element.textContent || element.innerText || ''
  }, [])

  const filteredMentionUsers = React.useMemo(() => {
    if (mentionQuery === null) {
      return []
    }

    const keyword = mentionQuery.trim().toLowerCase()
    if (!keyword) {
      return mentionableUsers
    }

    return mentionableUsers.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
      return (
        fullName.includes(keyword) ||
        user.firstName.toLowerCase().includes(keyword) ||
        user.lastName.toLowerCase().includes(keyword)
      )
    })
  }, [mentionQuery, mentionableUsers])

  const handleEditorChange = React.useCallback(
    (value: string) => {
      onTyping(value)
      const plainText = getPlainTextFromHtml(value)
      const match = plainText.match(/@([^\s@]*)$/)
      setMentionQuery(match ? match[1] : null)
    },
    [getPlainTextFromHtml, onTyping]
  )

  const handleSelectMentionUser = React.useCallback(
    (user: { firstName: string; lastName: string; profileId?: string; id: number }) => {
      const fullName = `${user.firstName}\u00A0${user.lastName}`.trim()
      const profileLink = `/user/${user.profileId || user.id}`
      const mentionHtml = `<a href="${profileLink}" class="mention font-bold text-blue-700 hover:underline">@${fullName}</a> `
      const updatedMessage = newMessage.replace(/@([^\s@]*)$/, mentionHtml)
      onTyping(updatedMessage)
      setMentionQuery(null)
      setSelectedMentionIndex(0)
      setTimeout(() => {
        if (inputRef.current?.focusAtEnd) {
          inputRef.current.focusAtEnd()
        } else {
          inputRef.current?.focus()
        }
      }, 0)
    },
    [inputRef, newMessage, onTyping]
  )

  React.useEffect(() => {
    setSelectedMentionIndex(0)
  }, [mentionQuery])

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionQuery !== null && filteredMentionUsers.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setSelectedMentionIndex(prev => (prev + 1) % filteredMentionUsers.length)
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setSelectedMentionIndex(prev => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length)
          return
        }

        if (event.key === 'Enter') {
          event.preventDefault()
          handleSelectMentionUser(filteredMentionUsers[selectedMentionIndex])
          return
        }

        if (event.key === 'Escape') {
          event.preventDefault()
          setMentionQuery(null)
          return
        }
      }
    },
    [filteredMentionUsers, handleSelectMentionUser, mentionQuery, selectedMentionIndex]
  )

  // Check if selected file is an image
  const isImageSelected = React.useMemo(
    () => !!(selectedFile && selectedFile.type.startsWith('image/')),
    [selectedFile]
  )

  // Create preview URL for image files
  const selectedFilePreviewUrl = React.useMemo(() => {
    if (!isImageSelected || !selectedFile) return undefined
    return URL.createObjectURL(selectedFile)
  }, [isImageSelected, selectedFile])

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (selectedFilePreviewUrl) URL.revokeObjectURL(selectedFilePreviewUrl)
    }
  }, [selectedFilePreviewUrl])

  const handleSendMessage = () => {
    if ((newMessage.trim() || selectedFile) && !isDisabled) {
      if (replyingToMessage) {
        onReplyMessage()
      } else {
        onSendMessage()
      }
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
    onFileChange(event)
  }

  // Check if we can send (either has message or has file)
  const canSend = (newMessage.trim() || selectedFile) && !isDisabled

  return (
    <div className="p-3 border-t border-gray-200 bg-white">
      {/* File Preview */}
      {selectedFile && (
        <div className="flex items-start gap-3 p-3 mb-3 bg-gray-50 rounded-lg border">
          {isImageSelected && selectedFilePreviewUrl ? (
            <Image
              src={selectedFilePreviewUrl}
              alt={selectedFile.name}
              width={48}
              height={48}
              className="rounded-md object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">📎</span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</div>
                <div className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button onClick={onRemoveFile} className="p-1 text-gray-400 hover:text-red-500 rounded">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Preview */}
      {replyingToMessage && (
        <div className="mb-3">
          <ReplyPreview replyingToMessage={replyingToMessage} onCancelReply={onCancelReply} />
        </div>
      )}

      {/* Input Area */}
      <div className="flex items-end gap-2">
        {/* File Upload Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled}
          className="p-2 text-gray-600 hover:text-blue-600 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg hover:bg-blue-50"
        >
          <Paperclip className="w-5 h-5" />
        </button>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,video/*,.pdf,.doc,.docx,.txt"
        />

        {/* Message Input */}
        <div className="flex-1 relative">
          <RichTextarea
            ref={inputRef}
            value={newMessage}
            onChange={handleEditorChange}
            onKeyDown={handleInputKeyDown}
            onKeyPress={handleKeyPress}
            onFocus={onInputFocus}
            onImagePaste={onFileSelect}
            placeholder={
              pendingTargetUserId && !selectedConversationId ? `Message ${targetUserName}...` : 'Type a message...'
            }
            disabled={isDisabled}
            className="w-full min-h-[40px] max-h-[120px] resize-none text-sm border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />

          {mentionQuery !== null && filteredMentionUsers.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-20">
              {filteredMentionUsers.map(user => (
                <button
                  key={user.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm ${
                    filteredMentionUsers[selectedMentionIndex]?.id === user.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleSelectMentionUser(user)}
                >
                  <span className="font-medium">
                    {user.firstName} {user.lastName}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Emoji Button */}
        <EmojiPicker
          onEmojiSelect={insertEmoji}
          onCustomEmojiSelect={insertCustomEmoji}
          buttonIconClassName="!size-4"
          buttonClassName="!size-9 rounded-full hover:bg-gray-100"
        />

        {/* Send Button */}
        <button
          onClick={handleSendMessage}
          disabled={!canSend}
          className={`p-2 rounded-lg transition-colors relative ${
            canSend ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={selectedFile && !newMessage.trim() ? 'Send file' : 'Send message'}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>

      {/* Upload Status */}
      {isUploadingFile && (
        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
          <div className="w-3 h-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Uploading file...
        </div>
      )}
    </div>
  )
}
